import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import UpgradeCrewSizeModal from "../Modals/UpgradeCrewSizeModal";
import UpgradePaymentModal from "../Modals/UpgradePaymentModal";
import UpgradeSubmitConfirmModal from "../Modals/UpgradeSubmitConfirmModal";
import messages from "../../../../assets/locale/messages";
import { createCompanyPortalPaymentRequest } from "../../../../network/apis/companyPortalEvents";
import { getPortalApiErrorMessage } from "../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import { PAYMENT_METHODS } from "../../Form/Steps/PublishAndPay/publishPayConstants";

const UPGRADE_STEPS = {
  size: "size",
  payment: "payment",
  confirm: "confirm",
};

/** `request_type` of a crew size change request (PROLINE-4060). */
export const UPGRADE_REQUEST_TYPE = "upgrade_size";

/**
 * Company "Upgrade Crew Size" flow (PROLINE-3932): choose a bigger crew size, pay the
 * difference by bank transfer, confirm. Mounted while open; starts on the size step.
 */
const CrewSizeUpgradeFlow = ({ event, currentCrewSize, atCapacity, onClose, onSubmitted }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];
  const [step, setStep] = useState(UPGRADE_STEPS.size);
  const [newCrewSize, setNewCrewSize] = useState(null);
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // The backend prices the upgrade at submission; no amount is sent.
  const submit = async () => {
    setSubmitting(true);
    try {
      const response = await createCompanyPortalPaymentRequest(
        {
          event_id: event.id,
          request_type: UPGRADE_REQUEST_TYPE,
          crew_size_plan_id: newCrewSize.id,
          payment_type: PAYMENT_METHODS.offline,
          payment_proof_url: evidenceUrl,
        },
        // The error is toasted once below, not also by the interceptor.
        { handlerEnabled: false },
      );
      // The backend's message, or ours while it sends none.
      const message = response?.data?.message;
      showSuccessMsg(message || "upgradeRequestSubmittedSuccessfully", Boolean(message));
      onSubmitted();
    } catch (error) {
      // The reason the backend gives: request already pending, no days left, ...
      showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Mounted per step so each popup starts clean (selection, uploaded evidence). */}
      {step === UPGRADE_STEPS.size && (
        <UpgradeCrewSizeModal
          open
          event={event}
          currentCrewSize={currentCrewSize}
          atCapacity={atCapacity}
          onClose={onClose}
          onContinue={(crewSize) => {
            setNewCrewSize(crewSize);
            setStep(UPGRADE_STEPS.payment);
          }}
        />
      )}
      {/* Hidden behind the confirmation but kept mounted, so cancelling returns to its evidence. */}
      {(step === UPGRADE_STEPS.payment || step === UPGRADE_STEPS.confirm) && (
        <UpgradePaymentModal
          open={step === UPGRADE_STEPS.payment}
          newCrewSizeName={newCrewSize.name}
          onClose={onClose}
          onSubmit={(url) => {
            setEvidenceUrl(url);
            setStep(UPGRADE_STEPS.confirm);
          }}
        />
      )}
      {step === UPGRADE_STEPS.confirm && (
        <UpgradeSubmitConfirmModal
          open
          newCrewSizeName={newCrewSize.name}
          submitting={submitting}
          onClose={() => setStep(UPGRADE_STEPS.payment)}
          onConfirm={submit}
        />
      )}
    </>
  );
};

CrewSizeUpgradeFlow.propTypes = {
  event: PropTypes.object.isRequired,
  currentCrewSize: PropTypes.shape({
    key: PropTypes.string.isRequired,
    max: PropTypes.number.isRequired,
    name: PropTypes.string,
  }).isRequired,
  /** The event already uses its whole capacity. */
  atCapacity: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called once the request is submitted. */
  onSubmitted: PropTypes.func.isRequired,
};

CrewSizeUpgradeFlow.defaultProps = {
  atCapacity: false,
};

export default CrewSizeUpgradeFlow;
